import React, {useRef, useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {
  CategorySection,
  CategoryHeader,
  CategoryTitle,
  CategoryName,
  CategorySubtitle,
  CategoryNav,
  CategoryArrow,
  CategoryScroller,
  CategoryTrack
} from 'routes/App/discountsV2/RecommendedDiscounts/DiscountCategoryCarousel/styles';

const SCROLL_STEP = 320;

const DiscountCategoryCarousel = ({category, children}) => {
  const scrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  useEffect(() => {
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', updateScrollState);
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, []);

  const scrollBy = (delta) => {
    scrollerRef.current?.scrollBy({left: delta, behavior: 'smooth'});
  };

  const hasOverflow = canScrollLeft || canScrollRight;

  return (
    <CategorySection>
      <CategoryHeader>
        <CategoryTitle>
          <div>
            <CategoryName>{category.name}</CategoryName>
            <CategorySubtitle>{category.subtitle}</CategorySubtitle>
          </div>
        </CategoryTitle>
        {hasOverflow && (
          <CategoryNav>
            <CategoryArrow
              type='button'
              onClick={() => scrollBy(-SCROLL_STEP)}
              disabled={!canScrollLeft}
              aria-label='Scroll left'
            >
              ‹
            </CategoryArrow>
            <CategoryArrow
              type='button'
              onClick={() => scrollBy(SCROLL_STEP)}
              disabled={!canScrollRight}
              aria-label='Scroll right'
            >
              ›
            </CategoryArrow>
          </CategoryNav>
        )}
      </CategoryHeader>
      <CategoryScroller ref={scrollerRef}>
        <CategoryTrack>{children}</CategoryTrack>
      </CategoryScroller>
    </CategorySection>
  );
};

DiscountCategoryCarousel.propTypes = {
  category: PropTypes.shape({
    name: PropTypes.string.isRequired,
    subtitle: PropTypes.string
  }).isRequired,
  children: PropTypes.node.isRequired
};

export default DiscountCategoryCarousel;
